import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

interface MasterConfirmDialogProps {
  visible: boolean;
  /** Red for a deletion, amber for a deactivation -- the reference's two -- and green for a save that succeeded. */
  tone: 'danger' | 'warning' | 'success';
  /** PrimeIcons name for the tile, e.g. "pi-trash". */
  icon: string;
  title: string;
  /** Plain text, or text with emphasis -- the template Draft Saved popup bolds the code and name (2026-10-14). */
  message: ReactNode;
  confirmLabel: string;
  /**
   * The dismissing verb.
   *
   * "Cancel" on a deletion; the deactivation reads "Keep as is" in
   * `design-refs/master/<master>-html/deactivate-this.html`, which says what
   * declining actually does rather than naming the act of declining.
   */
  cancelLabel?: string;
  busy?: boolean;
  /** Under the message: a hierarchy delete's list of what else goes (DeleteImpactList). */
  details?: ReactNode;
  /** The confirm action is held back, e.g. until that list has been read. */
  confirmDisabled?: boolean;
  /**
   * A refused delete, drawn as `design-refs/Deletepopup.html` draws it: `title` ("Can't delete …") and `lead`
   * take the place of the question and its message, `body` (DeleteBlockedNotice) runs the full width below them
   * in place of `details`, and the footer is one "Got it" that closes the dialog. Absent, the confirmation is
   * drawn as before.
   */
  blocked?: { title: string; lead: string; body: ReactNode };
  /**
   * One button and no Cancel: a result to acknowledge rather than a question (Supplier Created / Updated,
   * 2026-10-09). Closing the popup then calls `onConfirm` too.
   */
  single?: boolean;
  /** A further class on the dialog, for a screen that draws its own variant (Purchase Requests' saved popup). */
  className?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The reference's confirmation: a tinted icon tile, a question, a sentence
 * saying what the act does to existing documents, then Cancel and the verb.
 *
 * One component for both because the two differ only by tone, glyph and words
 * -- the layout the reference draws is identical, and a second copy would let
 * them drift apart.
 */
export function MasterConfirmDialog({
  visible,
  tone,
  icon,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  busy = false,
  details,
  confirmDisabled = false,
  blocked,
  single = false,
  className,
  onCancel,
  onConfirm,
}: MasterConfirmDialogProps) {
  /*
    While it closes, the dialog keeps what it last showed. "Got it" clears the refusal and the target together,
    and without this the closing animation briefly redrew the "Delete …?" confirmation in the card's place.
  */
  const [lastBlocked, setLastBlocked] = useState(blocked);
  if (visible && lastBlocked !== blocked) setLastBlocked(blocked);
  const shownBlocked = visible ? blocked : lastBlocked;
  const shownTitle = shownBlocked?.title ?? title;
  const dismiss = single ? onConfirm : onCancel;

  return (
    <Dialog
      /* Scoped backdrop: the mask is portalled to <body>, so no ancestor can reach it. */
      maskClassName="ms-mask"
      visible={visible}
      modal
      draggable={false}
      resizable={false}
      className={[shownBlocked ? 'ms-confirm ms-confirm--blocked' : 'ms-confirm', className].filter(Boolean).join(' ')}
      /*
        The header carries only the close control, as the reference draws it --
        the question itself is in the body. `aria-label` rather than a
        visually-hidden header copy, so the title is announced once rather than
        twice.
      */
      showHeader
      header={null}
      aria-label={shownTitle}
      onHide={dismiss}
      footer={
        <div className="flex justify-content-end gap-3">
          {shownBlocked ? (
            <Button type="button" label="Got it" onClick={onCancel} />
          ) : (
            <>
              {single ? null : <Button type="button" label={cancelLabel} outlined onClick={onCancel} />}
              <Button
                type="button"
                label={confirmLabel}
                loading={busy}
                disabled={confirmDisabled}
                onClick={onConfirm}
              />
            </>
          )}
        </div>
      }
    >
      <div className="ms-confirm__body">
        <span className={`ms-confirm__icon ms-confirm__icon--${tone}`} aria-hidden="true">
          <i className={`pi ${icon}`} />
        </span>
        <div className="ms-confirm__text">
          <p className="ms-confirm__title">{shownTitle}</p>
          {shownBlocked ? (
            <p className="ms-confirm__message">{shownBlocked.lead}</p>
          ) : (
            <>
              <p className="ms-confirm__message">{message}</p>
              {details}
            </>
          )}
        </div>
      </div>
      {/* The card's rows and warning run the dialog's full width, below the tile and title -- not under the title. */}
      {shownBlocked ? shownBlocked.body : null}
    </Dialog>
  );
}
